import React, { useState } from 'react';
import { RefreshCw, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import Wrapper from '../../../components/Wrapper';
import { useDashboardData } from './hooks/useDashboardData';
import ExecutiveHero from './components/ExecutiveHero';
import CriticalAlerts from './components/CriticalAlerts';
import CapacityIntelligence from './components/CapacityIntelligence';
import OrderIntelligence from './components/OrderIntelligence';
import InventoryHealthRadar from './components/InventoryHealthRadar';
import WorkforceProduction from './components/WorkforceProduction';
import HardwareStockPanel from './components/HardwareStockPanel';
import DashboardExplorer from './components/DashboardExplorer';

// ─── Skeleton ──────────────────────────────────────────────────────────────────
const Sk = ({ className }) => (
  <div className={`animate-pulse bg-slate-200 rounded-xl ${className}`} />
);

const DashboardSkeleton = () => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {[...Array(6)].map((_, i) => <Sk key={i} className="h-20" />)}
    </div>
    <Sk className="h-10" />
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Sk className="h-44" /><Sk className="h-44" />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Sk className="h-52" /><Sk className="h-52" />
    </div>
    <Sk className="h-64" />
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Sk className="h-72" /><Sk className="h-72" />
    </div>
    <Sk className="h-48" />
  </div>
);

// ─── Error state ───────────────────────────────────────────────────────────────
const ErrorState = ({ message, onRetry }) => (
  <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 p-8">
    <div className="w-12 h-12 rounded-full bg-red-50 border border-red-200 flex items-center justify-center">
      <span className="text-red-600 text-xl font-bold">!</span>
    </div>
    <p className="text-lg font-semibold text-slate-800">Failed to load dashboard</p>
    <p className="text-sm text-slate-500 text-center max-w-md">{message}</p>
    <button
      onClick={onRetry}
      className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm font-medium hover:bg-slate-700 transition-colors"
    >
      Try again
    </button>
  </div>
);

// ─── Date helpers ──────────────────────────────────────────────────────────────
const toDateStr = (d) => d.toISOString().split('T')[0];
const todayStr  = toDateStr(new Date());

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function LabDashboardPage() {
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [spinning, setSpinning]         = useState(false);

  const { data, loading, error, lastUpdated, refresh } = useDashboardData(selectedDate);

  const handleRefresh = async () => {
    setSpinning(true);
    try { await refresh(); } finally { setTimeout(() => setSpinning(false), 600); }
  };

  const shiftDate = (days) => {
    const d = new Date(selectedDate + 'T00:00:00');
    d.setDate(d.getDate() + days);
    setSelectedDate(toDateStr(d));
  };

  return (
    <Wrapper>
      <div className="space-y-5 pb-20 lg:pb-6">

        {/* ── Page header: title LEFT · controls RIGHT ─────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
          {/* Left */}
          <div>
            <h2 className="m-0 text-2xl font-bold text-slate-800">Lab Dashboard</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Manufacturing &amp; laboratory operations
              {lastUpdated && (
                <span className="ml-2 text-slate-400">
                  · Updated {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </p>
          </div>

          {/* Right: date navigator + refresh */}
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl px-2 py-1.5 shadow-sm">
              <button
                onClick={() => shiftDate(-1)}
                className="p-1 rounded hover:bg-slate-100 text-slate-500 transition-colors"
                title="Previous day"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-1.5 px-1">
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                <input
                  type="date"
                  value={selectedDate}
                  max={todayStr}
                  onChange={(e) => e.target.value && setSelectedDate(e.target.value)}
                  className="text-xs font-medium text-slate-700 bg-transparent border-0 outline-none cursor-pointer w-[120px]"
                />
              </div>
              <button
                onClick={() => shiftDate(1)}
                disabled={selectedDate >= todayStr}
                className="p-1 rounded hover:bg-slate-100 text-slate-500 transition-colors disabled:opacity-30"
                title="Next day"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {selectedDate !== todayStr && (
              <button
                onClick={() => setSelectedDate(todayStr)}
                className="text-xs px-3 py-2 bg-slate-800 text-white rounded-xl font-medium hover:bg-slate-700 transition-colors"
              >
                Today
              </button>
            )}

            <button
              onClick={handleRefresh}
              disabled={loading}
              title="Refresh"
              className="p-2 rounded-xl bg-white border border-slate-200 text-slate-500 hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${(loading || spinning) ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {loading && <DashboardSkeleton />}
        {error && !loading && <ErrorState message={error} onRetry={refresh} />}

        {!loading && !error && data && (
          <>
            {/* ── KPI Hero tiles ──────────────────────────────────────────── */}
            <ExecutiveHero data={data} />

            {/* ── Critical alerts ─────────────────────────────────────────── */}
            <CriticalAlerts data={data} />

            {/* ── Hardware Stock (LEFT, compact) + Data Explorer (RIGHT) ──── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <section id="hardware-stock">
                <HardwareStockPanel data={data} />
              </section>
              <section id="explorer">
                <DashboardExplorer data={data} />
              </section>
            </div>

            {/* ── Capacity intelligence ────────────────────────────────────── */}
            <section id="capacity">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-4 w-1 rounded-full bg-blue-500" />
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Capacity Intelligence</h3>
              </div>
              <CapacityIntelligence data={data} />
            </section>

            {/* ── Orders ──────────────────────────────────────────────────── */}
            <section id="orders">
              <OrderIntelligence data={data} />
            </section>

            {/* ── Inventory (left) | Workforce (right) ─────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
              <section id="inventory">
                <InventoryHealthRadar data={data} />
              </section>
              <section id="workforce">
                <WorkforceProduction data={data} />
              </section>
            </div>
          </>
        )}
      </div>
    </Wrapper>
  );
}
